/** Shapes the /expenses API returns — see backend/src/modules/expenses. */

export type ExpenseKind = 'travel_allowance' | 'daily_allowance' | 'general';
export type ExpenseStatus = 'pending' | 'approved' | 'rejected' | 'disbursed' | 'cancelled';
export type ApprovalMode = 'admin_only' | 'two_level';

export interface ExpenseCategory {
  id: string;
  name: string;
  kind: ExpenseKind;
  /** Rupees, as a string. Per-km (travel) or per-day (daily); null otherwise. */
  rate: string | null;
  ratePaise: number | null;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
  claimCount?: number;
}

export interface ExpensePerson { id: string | null; name: string | null; }

export interface ExpenseClaim {
  id: string;
  user: { id: string; name: string };
  category: { id: string | null; name: string | null; kind: ExpenseKind };
  kind: ExpenseKind;
  expenseDate: string;
  /** Rupees, as a string (e.g. "123.40"). */
  amount: string;
  amountPaise: number;
  km: number | null;
  kmSource: 'gps' | 'odometer' | null;
  rate: string | null;
  notes: string | null;
  visitIds: string[];
  photos: { url: string }[];
  status: ExpenseStatus;
  approvalMode: ApprovalMode;
  pendingLevel: 'manager' | 'admin' | null;
  approver: ExpensePerson | null;
  managerDecidedBy: ExpensePerson | null;
  managerDecidedAt: string | null;
  decidedBy: ExpensePerson | null;
  decidedAt: string | null;
  decisionNote: string | null;
  disbursedBy: ExpensePerson | null;
  disbursedAt: string | null;
  disbursementRef: string | null;
  createdAt: string;
}

export interface ExpenseBucket { count: number; total: string; totalPaise: number; }

export interface ExpenseDashboard {
  from: string;
  to: string;
  created: ExpenseBucket;
  pending: ExpenseBucket;
  approved: ExpenseBucket;
  disbursed: ExpenseBucket;
  rejected: ExpenseBucket;
}

export const KIND_LABEL: Record<ExpenseKind, string> = {
  travel_allowance: 'Travel Allowance',
  daily_allowance: 'Daily Allowance',
  general: 'Expense',
};

/** ₹ with Indian grouping, from a rupee string. */
export function rupees(amount: string | number): string {
  const n = Number(amount);
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
